import { useState, useEffect, useRef } from 'react';

import "../../Styles/Colorize/colorTooltip.scss";

const ColorTooltip = ({ colors = [], colorsDuo = [], colorsTrio = [],
  // Optional conversion functions that can be passed in
  convertRgbToHex = null,
  convertHslToRgb = null
}) => {
  // State for cursor position
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // State for color info - default to not visible
  const [tooltipVisible, setTooltipVisible] = useState(false);
  // Update the initial state to include gradient info
  const [colorInfo, setColorInfo] = useState({
    isGradient: false,
    isMultiMatch: false, 
    gradientMatches: [],
    matches: [],          
    variableName: '',
    colorValue: '',
    hexValue: '',
    propertyName: '',
    elementInfo: ''
  });

  // Create map of color values to variable names
  const colorMapRef = useRef({});

  // Track the previous color arrays for comparison
  const prevColorsRef = useRef({ colors, colorsDuo, colorsTrio });

  // Previous element ref for optimization
  const lastElementRef = useRef(null);

  const getDisplayPropertyName = (propertyName) => {
    const propertyMap = {
      'backgroundColor': 'Background',
      'color': 'Text',
      'borderColor': 'Border',
      'borderTopColor': 'Border top',
      'borderRightColor': 'Border right',
      'borderBottomColor': 'Border bottom',
      'borderLeftColor': 'Border left',
      'outlineColor': 'Outline',
      'fill': 'SVG',
      'stroke': 'SVG',
      'gradient': 'Gradient'
    };

    return propertyMap[propertyName] || propertyName;
  };

  // Helper to safely get element class name string
  const getElementClassString = (element) => {
    if (!element) return '';

    // Handle different className types
    if (typeof element.className === 'string') {
      return element.className ? '.' + element.className.replace(/\s+/g, '.') : '';
    } else if (typeof element.className === 'object' && element.className.baseVal) {
      // SVG elements have className.baseVal
      return element.className.baseVal ? '.' + element.className.baseVal.replace(/\s+/g, '.') : '';
    }

    return '';
  };

  // Default RGB to Hex conversion if none provided
  const defaultRgbToHex = (r, g, b) => {
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  };

  // Default HSL to RGB conversion if none provided
  const defaultHslToRgb = (h, s, l) => {
    s /= 100;
    l /= 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, (h / 360) + 1 / 3);
      g = hue2rgb(p, q, h / 360);
      b = hue2rgb(p, q, (h / 360) - 1 / 3);
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
  };

  // Use provided conversion functions or fallback to defaults
  const rgbToHexFn = convertRgbToHex || defaultRgbToHex;
  const hslToRgbFn = convertHslToRgb || defaultHslToRgb;

  // Convert color to RGB format for matching computed styles
  const convertToRgb = (color) => {
    if (!color) return null;

    // If already RGB format, return as is
    if (color.startsWith('rgb')) {
      return color;
    }

    // Convert hex to RGB
    if (color.startsWith('#')) {
      // Handle shorthand hex (#fff)
      let hex = color;
      if (hex.length === 4) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
      }

      // Parse hex value
      const r = parseInt(hex.substring(1, 3), 16);
      const g = parseInt(hex.substring(3, 5), 16);
      const b = parseInt(hex.substring(5, 7), 16);

      return `rgb(${r}, ${g}, ${b})`;
    }

    // Handle HSL colors
    if (color.startsWith('hsl')) {
      // Extract HSL values
      const match = color.match(/hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/);
      if (!match) return null;

      const h = parseInt(match[1]);
      const s = parseInt(match[2]);
      const l = parseInt(match[3]);

      // Convert HSL to RGB using the provided or default function
      const { r, g, b } = hslToRgbFn(h, s, l);

      return `rgb(${r}, ${g}, ${b})`;
    }

    return null;
  };

  // Convert RGB color to hex
  const rgbToHex = (rgbStr) => {
    if (!rgbStr || !rgbStr.startsWith('rgb')) return rgbStr;

    const match = rgbStr.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (!match) return rgbStr;

    const r = parseInt(match[1]);
    const g = parseInt(match[2]);
    const b = parseInt(match[3]);

    return rgbToHexFn(r, g, b);
  };

  // Helper function to check if arrays are different
  const areArraysDifferent = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return true;
    if (a.length !== b.length) return true;
    
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return true;
    }
    
    return false;
  };

  // Set up color mapping - runs when color arrays change
  useEffect(() => {
    // Skip empty initializations
    if (
      (!Array.isArray(colors) || colors.length === 0) &&
      (!Array.isArray(colorsDuo) || colorsDuo.length === 0) &&
      (!Array.isArray(colorsTrio) || colorsTrio.length === 0)
    ) {
      return;
    }

    // Check if any color array has changed
    const prevColors = prevColorsRef.current;
    const hasColorsChanged = areArraysDifferent(colors, prevColors.colors);
    const hasColorsDuoChanged = areArraysDifferent(colorsDuo, prevColors.colorsDuo);
    const hasColorsTrioChanged = areArraysDifferent(colorsTrio, prevColors.colorsTrio);

    // Skip if no colors have changed
    if (!hasColorsChanged && !hasColorsDuoChanged && !hasColorsTrioChanged) {
      return;
    }

    // Create a new color map (don't mutate the existing one)
    const colorMap = {};

    // Define variable names
    const colorNames = ['c50', 'c100', 'c200', 'c300', 'c400', 'c500', 'c600', 'c700', 'c800', 'c900', 'c950'];
    const duoNames = ['d50', 'd100', 'd200', 'd300', 'd400', 'd500', 'd600', 'd700', 'd800', 'd900', 'd950'];
    const trioNames = ['e50', 'e100', 'e200', 'e300', 'e400', 'e500', 'e600', 'e700', 'e800', 'e900', 'e950'];

    // Add primary colors to map
    if (Array.isArray(colors) && colors.length > 0) {
      colors.forEach((color, index) => {
        if (color && color !== "#000" && color !== "transparent") {
          const variableName = colorNames[index % colorNames.length];
          colorMap[color.toLowerCase()] = { variableName, originalColor: color };

          // Also create RGB version for computed style matching
          const rgbVersion = convertToRgb(color);
          if (rgbVersion) {
            colorMap[rgbVersion.toLowerCase()] = { variableName, originalColor: color };
          }

        }
      });
    }

    // Add duo colors to map
    if (Array.isArray(colorsDuo) && colorsDuo.length > 0) {
      colorsDuo.forEach((color, index) => {
        if (color && color !== "#000" && color !== "transparent") {
          const variableName = duoNames[index % duoNames.length];
          colorMap[color.toLowerCase()] = { variableName, originalColor: color };

          // Also create RGB version for computed style matching
          const rgbVersion = convertToRgb(color);
          if (rgbVersion) {
            colorMap[rgbVersion.toLowerCase()] = { variableName, originalColor: color };
          }

        }
      });
    }

    // Add trio colors to map
    if (Array.isArray(colorsTrio) && colorsTrio.length > 0) {
      colorsTrio.forEach((color, index) => {
        if (color && color !== "#000" && color !== "transparent") {
          const variableName = trioNames[index % trioNames.length];
          colorMap[color.toLowerCase()] = { variableName, originalColor: color };

          // Also create RGB version for computed style matching
          const rgbVersion = convertToRgb(color);
          if (rgbVersion) {
            colorMap[rgbVersion.toLowerCase()] = { variableName, originalColor: color };
          }

        }
      });
    }

    colorMapRef.current = colorMap;
    
    // Update the previous colors reference for future comparisons
    prevColorsRef.current = { colors, colorsDuo, colorsTrio };
    
  }, [colors, colorsDuo, colorsTrio]);

  const isWhiteOrNearWhite = (color) => {
    if (!color) return false;

    // Direct check for common white values
    if (color === 'rgb(255, 255, 255)' || color === '#ffffff' || color === '#fff' || color === 'white') {
      return true;
    }

    // NEW: Quick check if this color is in our palette - don't skip palette colors
    const colorMap = colorMapRef.current;
    const normalizedColor = color.toLowerCase().trim();
    if (colorMap[normalizedColor]) {
      return false; // Don't skip colors that are in our palette
    }

    // Check RGB values for near-white (very high values in all channels)
    if (color.startsWith('rgb')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);

        if (r > 240 && g > 240 && b > 240) {
          return true;
        }
      }
    }

    return false;
  };

  // Add this helper function to check if a color is black
  const isBlackColor = (color) => {
    if (!color) return false;

    if (color === 'rgb(0, 0, 0)' ||
      color === '#000000' ||
      color === '#000' ||
      color === 'black') {
      return true;
    }

    if (color.startsWith('rgb')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);

        if (r <= 5 && g <= 5 && b <= 5) {
          return true;
        }
      }
    }

    return false;
  };

  // Add this helper function to check if an element is SVG-related
  // const isSvgElement = (element) => {
  //   if (!element) return false;

  //   const tagName = element.tagName?.toLowerCase();

  //   if (tagName === 'svg' ||
  //     tagName === 'path' ||
  //     tagName === 'rect' ||
  //     tagName === 'circle' ||
  //     tagName === 'ellipse' ||
  //     tagName === 'line' ||
  //     tagName === 'polyline' ||
  //     tagName === 'polygon' ||
  //     tagName === 'text' ||
  //     tagName === 'g') {
  //     return true;
  //   }

  //   return element.closest('svg') !== null;
  // };

  // Find variable name for a color
  const findVariableForColor = (color, propName) => {
    if (!color) return null;

    // Skip white/near-white colors
    if (isWhiteOrNearWhite(color)) {
      return null;
    }

    if (isBlackColor(color)) {
      return null;
    }

    // ADD THIS DEBUGGING:
    const colorMap = colorMapRef.current;


    const normalizedColor = color.toLowerCase().trim();
    // const colorMap = colorMapRef.current;
    // const normalizedColor = color.toLowerCase().trim();

    // Direct match
    if (colorMap[normalizedColor]) {
      const { variableName, originalColor } = colorMap[normalizedColor];
      return {
        variableName,
        colorValue: color,
        originalColor,
        hexValue: originalColor.startsWith('#') ? originalColor : rgbToHex(color),
        propertyName: propName
      };
    }

    // If it's an RGB color, try different formats
    if (normalizedColor.startsWith('rgb')) {
      // Try without spaces
      const noSpaces = normalizedColor.replace(/\s+/g, '');
      if (colorMap[noSpaces]) {
        const { variableName, originalColor } = colorMap[noSpaces];
        return {
          variableName,
          colorValue: color,
          originalColor,
          hexValue: originalColor.startsWith('#') ? originalColor : rgbToHex(color),
          propertyName: propName
        };
      }

      // Extract RGB components
      const match = normalizedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);

        // Try standard format
        const alternateFormat = `rgb(${r}, ${g}, ${b})`;
        if (colorMap[alternateFormat.toLowerCase()]) {
          const { variableName, originalColor } = colorMap[alternateFormat.toLowerCase()];
          return {
            variableName,
            colorValue: color,
            originalColor,
            hexValue: originalColor.startsWith('#') ? originalColor : rgbToHex(color),
            propertyName: propName
          };
        }

        // Try close matches
        let bestMatch = null;
        let smallestDistance = Infinity;

        // Only do this if we have colors to check
        if (Object.keys(colorMap).length > 0) {
          const colorEntries = Object.entries(colorMap);

          for (const [mappedColor, data] of colorEntries) {
            if (mappedColor.startsWith('rgb')) {
              const mappedMatch = mappedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
              if (mappedMatch) {
                const mr = parseInt(mappedMatch[1]);
                const mg = parseInt(mappedMatch[2]);
                const mb = parseInt(mappedMatch[3]);

                // Calculate color distance
                const distance = Math.sqrt(
                  Math.pow(r - mr, 2) +
                  Math.pow(g - mg, 2) +
                  Math.pow(b - mb, 2)
                );

                if (distance < smallestDistance) {
                  smallestDistance = distance;
                  bestMatch = {
                    color: mappedColor,
                    variableName: data.variableName,
                    originalColor: data.originalColor,
                    distance
                  };
                }
              }
            }
          }
        }

        // Accept close matches (distance < 5)
        if (bestMatch && bestMatch.distance < 5) {
          return {
            variableName: bestMatch.variableName,
            colorValue: color,
            originalColor: bestMatch.originalColor,
            hexValue: bestMatch.originalColor.startsWith('#') ? bestMatch.originalColor : rgbToHex(color),
            propertyName: propName
          };
        }
      }
    }

    return null;
  };

  // Add this utility function to quickly check if an element is likely to have colors
  const isKnownColorElement = (element) => {
    // Quick check for element's inline styles
    const style = element.style;

    // Check for any inline style color properties
    if (style.backgroundColor ||
      style.color ||
      style.borderColor ||
      style.fill ||
      style.stroke ||
      (style.background && style.background.includes('gradient'))) {
      return true;
    }

    // Check for known color-related class names (customize based on your app)
    const className = typeof element.className === 'string'
      ? element.className
      : (element.className?.baseVal || '');

    if (className.includes('color') ||
      className.includes('bg-') ||
      className.includes('text-')) {
      return true;
    }

    // Check for SVG elements which often have colors
    if (element.tagName === 'svg' ||
      element.tagName === 'path' ||
      element.tagName === 'rect' ||
      element.tagName === 'circle') {
      return true;
    }

    return false;
  };

  // Helper function to check if an element is an actual image (not SVG)
  // Combined function to check for images and handle transparency
  const shouldSkipForImage = (element, x, y) => {
    if (!element) return false;

    if (element.tagName === 'IMG') {
      const src = element.getAttribute('src') || '';
      if (src.toLowerCase().endsWith('.svg')) {
        return false; // Don't skip SVG images
      }

      // NEW: Check if the image has border styles - if so, don't skip
      const style = window.getComputedStyle(element);
      const hasBorder = style.borderColor &&
        style.borderColor !== 'transparent' &&
        style.borderColor !== 'rgba(0, 0, 0, 0)' &&
        style.borderWidth &&
        style.borderWidth !== '0px';

      if (hasBorder) {
        return false; // Don't skip images with borders
      }

      // IMPORTANT: Check pixel transparency for all other images
      // Return true (skip tooltip) ONLY if the pixel is NOT transparent
      return !isTransparentPixel(element, x, y);
    }


    // For background images, we need a different approach
    // since we can't directly access the pixel data
    const style = window.getComputedStyle(element);
    const backgroundImage = style.backgroundImage || '';

    if (backgroundImage &&
      backgroundImage !== 'none' &&
      !backgroundImage.includes('svg') &&
      !backgroundImage.includes('gradient')) {

      // For background images:
      // 1. Check if element has a specific background-color
      const bgColor = style.backgroundColor;
      if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
        // If it has a background color, check for child elements
        // that might be under this point
        element.style.pointerEvents = 'none';
        const elementBeneath = document.elementFromPoint(x, y);
        element.style.pointerEvents = '';

        // If we found a different element beneath, then this point
        // might be transparent in the background image
        if (elementBeneath && elementBeneath !== element) {
          return false; // Don't skip, might be transparent part
        }
      }

      // Default: assume background images are opaque
      return true;
    }

    return false;
  };

  // you need this to check on images, which part of the image is actually transparent, like in pngs
  // Utility to check pixel transparency
  // Enhanced pixel transparency detection
  const isTransparentPixel = (() => {
    // Create a canvas once for reuse
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    return (imgElement, x, y) => {
      if (!imgElement || !imgElement.complete || imgElement.naturalWidth === 0) {
        return true; // Consider unloaded images as transparent to be safe
      }

      try {
        // Get the position of the image in the viewport
        const rect = imgElement.getBoundingClientRect();

        // Calculate the relative position within the image
        // Account for device pixel ratio for high-DPI displays
        // const pixelRatio = window.devicePixelRatio || 1;

        // Calculate the exact position in the original image coordinates
        // This handles scaling between the displayed size and the natural size
        const scaleX = imgElement.naturalWidth / rect.width;
        const scaleY = imgElement.naturalHeight / rect.height;

        const imgX = Math.round((x - rect.left) * scaleX);
        const imgY = Math.round((y - rect.top) * scaleY);

        // Check bounds
        if (imgX < 0 || imgY < 0 || imgX >= imgElement.naturalWidth || imgY >= imgElement.naturalHeight) {
          return true; // Out of bounds is considered transparent
        }

        // Set canvas to the size of a small region around the pixel
        // This helps with sampling accuracy
        const sampleSize = 3; // Sample a 3x3 pixel area
        canvas.width = sampleSize;
        canvas.height = sampleSize;

        // Calculate area to sample
        const sampleX = Math.max(0, imgX - Math.floor(sampleSize / 2));
        const sampleY = Math.max(0, imgY - Math.floor(sampleSize / 2));
        const sampleWidth = Math.min(sampleSize, imgElement.naturalWidth - sampleX);
        const sampleHeight = Math.min(sampleSize, imgElement.naturalHeight - sampleY);

        // Clear and draw the region
        ctx.clearRect(0, 0, sampleSize, sampleSize);
        ctx.drawImage(
          imgElement,
          sampleX, sampleY, sampleWidth, sampleHeight,
          0, 0, sampleWidth, sampleHeight
        );

        // Get pixel data for the region
        const pixelData = ctx.getImageData(0, 0, sampleWidth, sampleHeight).data;

        // Check average alpha in the region
        let totalAlpha = 0;
        let pixelCount = 0;

        for (let i = 3; i < pixelData.length; i += 4) {
          totalAlpha += pixelData[i];
          pixelCount++;
        }

        const avgAlpha = totalAlpha / pixelCount;

        // More lenient threshold - consider it transparent if average alpha is low
        return avgAlpha < 25; // Alpha < 25 out of 255 is considered transparent
      } catch (e) {
        console.warn('Error checking pixel transparency:', e);
        // If there's an error, assume transparent to be safe
        return true;
      }
    };
  })();
  

  // Get element color properties in priority order
  const getElementColorProperties = (element) => {
    if (!element) return [];

    const style = window.getComputedStyle(element);

    // Order properties by visibility priority
    return [
      { name: 'backgroundColor', value: style.backgroundColor },
      { name: 'fill', value: style.fill },
      { name: 'color', value: style.color },
      { name: 'borderColor', value: style.borderColor },
      { name: 'stroke', value: style.stroke }
    ].filter(prop =>
      prop.value &&
      prop.value !== 'transparent' &&
      prop.value !== 'rgba(0, 0, 0, 0)'
    );
  };

  // Check element for color variables
  const findColorVariableForElement = (element) => {
    if (!element) return null;

    // Get element description safely
    const elementDesc = `${element.tagName}${getElementClassString(element)}`;

    // Get color properties
    const colorProperties = getElementColorProperties(element);


    // Collect all matches instead of returning the first one
    const allMatches = [];

    // gradient
    const style = window.getComputedStyle(element);

    // gradient
    if (style.background && style.background.includes('gradient')) {

      // Extract all colors from the gradient
      const gradientColors = extractGradientColors(style.background);

      if (gradientColors && gradientColors.length > 0) {
        // Collect all matches for gradient colors
        const matches = [];

        for (const colorStr of gradientColors) {
          const match = findVariableForColor(colorStr, 'gradient');
          if (match) {
            matches.push(match);
          }
        }

        // If we found at least one match, deduplicate and add as gradient info
        if (matches.length > 0) {
          // NEW: Remove duplicates based on variableName for gradients
          const uniqueGradientMatches = matches.filter((match, index, self) =>
            index === self.findIndex(m => m.variableName === match.variableName)
          );


          allMatches.push({
            isGradient: true,
            gradientMatches: uniqueGradientMatches, // Use deduplicated matches
            elementInfo: `${elementDesc} (gradient)`
          });
        }
      }
    }

    // Standard check - collect all matches
    for (const prop of colorProperties) {
      const match = findVariableForColor(prop.value, prop.name);
      if (match) {
        allMatches.push({
          ...match,
          elementInfo: elementDesc
        });
      }
    }


    if (allMatches.length > 0) {
    
      // Remove duplicates based on variableName AND propertyName
      const uniqueMatches = allMatches.filter((match, index, self) =>
        index === self.findIndex(m =>
          m.variableName === match.variableName &&
          m.propertyName === match.propertyName
        )
      );

    

      if (uniqueMatches.length === 1) {
        return uniqueMatches[0]; // Single match - return as before
      } else {
        // Multiple matches - return as a special multi-match object
        return {
          isMultiMatch: true,
          matches: uniqueMatches,
          elementInfo: elementDesc
        };
      }
    }
    return null;
  };

  // gradients
  // Extract colors from gradient string
  const extractGradientColors = (gradientString) => {
    // This regex specifically targets rgb/rgba values in a gradient
    const colorRegex = /rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)|\rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([0-9.]+)\s*\)/g;
    return gradientString.match(colorRegex) || [];
  };




  // Handle mouse movement
  useEffect(() => {

    const stackCache = new Map();
    let cacheCleanupTimer = null;

    const cachedPosition = { current: { x: 0, y: 0 } };
    let animationFrameId = null;

    const handleMouseMove = (e) => {
      // Update position
      setPosition({ x: e.clientX, y: e.clientY });

      const cacheKey = `${Math.round(e.clientX / 10)},${Math.round(e.clientY / 10)}`;

      if (stackCache.has(cacheKey)) {
        const cachedResult = stackCache.get(cacheKey);
        // If we have a cached result and it's recent (less than 500ms old)
        if (Date.now() - cachedResult.timestamp < 500) {
          // Use the cached color match
          const colorMatch = cachedResult.match;

          // Update tooltip with cached data
          if (colorMatch) {
            if (colorMatch.isGradient) {
              setColorInfo({
                isGradient: true,
                isMultiMatch: false,
                gradientMatches: colorMatch.gradientMatches,
                matches: [],
                elementInfo: colorMatch.elementInfo,
                variableName: '',
                colorValue: '',
                hexValue: '',
                propertyName: ''
              });
            } else if (colorMatch.isMultiMatch) {
              setColorInfo({
                isGradient: false,
                isMultiMatch: true,
                gradientMatches: [],
                matches: colorMatch.matches,
                elementInfo: colorMatch.elementInfo,
                variableName: '',
                colorValue: '',
                hexValue: '',
                propertyName: ''
              });
            } else {
              setColorInfo({
                isGradient: false,
                isMultiMatch: false,
                gradientMatches: [],
                matches: [],
                variableName: colorMatch.variableName,
                colorValue: colorMatch.colorValue,
                hexValue: colorMatch.hexValue,
                originalColor: colorMatch.originalColor,
                propertyName: colorMatch.propertyName,
                elementInfo: colorMatch.elementInfo
              });
            }
            setTooltipVisible(true);
            return; // Skip the expensive calculation
          }
        }
      }

      // start of new to check for all elements under the cursor if the element you hover over does not have a color
      const elements = [];
      let currentElement = document.elementFromPoint(e.clientX, e.clientY);

      if (currentElement && currentElement.classList.contains('tooltip-ignore')) {
        setTooltipVisible(false);
        return;
      }

      // ADD THE IMAGE CHECK RIGHT HERE
      // Check for transparency at the exact pixel
      if (shouldSkipForImage(currentElement, e.clientX, e.clientY)) {
        setTooltipVisible(false);
        return;
      }

      
















      // Skip if same element as last time and we already have a tooltip visible
      if (currentElement === lastElementRef.current && tooltipVisible) {
        return;
      }

      // Store current top element for optimization
      lastElementRef.current = currentElement;

      // Check if we're in modules
      if (!currentElement || !currentElement.closest('.modules')) {
        setTooltipVisible(false);
        return;
      }


      // With this optimized version
      let originalVisibility = [];
      let maxStackDepth = 5; // Limit the stack depth for performance

      while (currentElement && maxStackDepth > 0) {
        elements.push(currentElement);

        // Store original visibility
        originalVisibility.push({
          element: currentElement,
          visibility: currentElement.style.visibility
        });

        // Hide element (visibility is faster than pointer-events)
        currentElement.style.visibility = 'hidden';

        // Get next element
        currentElement = document.elementFromPoint(e.clientX, e.clientY);

        if (!currentElement ||
          elements.includes(currentElement) ||
          currentElement === document.body ||
          currentElement === document.documentElement) {
          break;
        }

        maxStackDepth--;
      }

      // Restore visibility to all changed elements
      originalVisibility.forEach(item => {
        item.element.style.visibility = item.visibility;
      });


      // Now check each element for colors
      let colorMatch = null;
      // nienke 1
      let matchedElement = null;

      for (const element of elements) {
        // Quick pre-check to skip elements unlikely to have colors
        // This avoids expensive getComputedStyle calls for elements that don't have colors
        if (!isKnownColorElement(element)) {
          continue;
        }

        // Skip elements with no color properties
        const colorProperties = getElementColorProperties(element);
        if (colorProperties.length === 0) {
          continue;
        }

        // Check this element for color matches
        // nienke 2
        colorMatch = findColorVariableForElement(element);
        if (colorMatch) {
          matchedElement = element;
          break;
        }
      }
      // end



      // Update state based on match result
      // In the handleMouseMove function, where you update state based on match result
      if (colorMatch) {

        stackCache.set(cacheKey, {
          match: colorMatch,
          timestamp: Date.now()
        });

        if (!cacheCleanupTimer) {
          cacheCleanupTimer = setTimeout(() => {
            stackCache.clear(); // Clear the entire cache
            cacheCleanupTimer = null; // Reset the timer reference
          }, 5000); // 5 seconds of inactivity before clearing
        }

        // Limit cache size to prevent memory issues
        if (stackCache.size > 100) {
          const oldestKey = Array.from(stackCache.keys())[0];
          stackCache.delete(oldestKey);
        }

        if (colorMatch.isGradient) {
          setColorInfo({
            isGradient: true,
            isMultiMatch: false,
            gradientMatches: colorMatch.gradientMatches,
            matches: [],
            elementInfo: colorMatch.elementInfo,
            variableName: '',
            colorValue: '',
            hexValue: '',
            propertyName: ''
          });
        } else if (colorMatch.isMultiMatch) {
          setColorInfo({
            isGradient: false,
            isMultiMatch: true,
            gradientMatches: [],
            matches: colorMatch.matches,
            elementInfo: colorMatch.elementInfo,
            variableName: '',
            colorValue: '',
            hexValue: '',
            propertyName: ''
          });
        } else {
          setColorInfo({
            isGradient: false,
            isMultiMatch: false,
            gradientMatches: [],
            matches: [],
            variableName: colorMatch.variableName,
            colorValue: colorMatch.colorValue,
            hexValue: colorMatch.hexValue,
            originalColor: colorMatch.originalColor,
            propertyName: colorMatch.propertyName,
            elementInfo: colorMatch.elementInfo
          });
        }
        setTooltipVisible(true);
      } else {
        setTooltipVisible(false);
      }
    };


    const smoothMouseMove = (e) => {
      // Always update the cached position with the latest coordinates
      cachedPosition.current = { x: e.clientX, y: e.clientY };

      // If we already have a frame scheduled, don't schedule another one
      if (animationFrameId) {
        return;
      }

      // Schedule the animation frame
      animationFrameId = requestAnimationFrame(() => {
        // Process the latest position in handleMouseMove
        handleMouseMove({
          clientX: cachedPosition.current.x,
          clientY: cachedPosition.current.y
        });

        // Reset the frame ID so we can schedule another one
        animationFrameId = null;
      });
    };

    // Add event listener
    document.addEventListener('mousemove', smoothMouseMove);

    // Cleanup
    // Cleanup
    return () => {
      document.removeEventListener('mousemove', smoothMouseMove);

      // Make sure to cancel any pending animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      // Clean up any cache timer
      if (cacheCleanupTimer) {
        clearTimeout(cacheCleanupTimer);
      }
    };
  }, []);

  /////////



  // Add click debugging
  useEffect(() => {
    const handleClick = (e) => {
      const element = document.elementFromPoint(e.clientX, e.clientY);
      if (element) {

        // Get computed styles
        // const style = window.getComputedStyle(element);

        // Manual match test
        findColorVariableForElement(element);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [tooltipVisible, colorInfo]);

  // Only render tooltip if visible
  if (!tooltipVisible) {
    return null;
  }

  // different spacings
  const itemCount = colorInfo.isGradient
    ? colorInfo.gradientMatches.length
    : colorInfo.isMultiMatch
      ? colorInfo.matches.length
      : 1;

  // Your existing height function
  const getTooltipHeight = (count) => {
    if (count === 1) return 95;
    if (count === 2) return 168;
    return 240; // for 3+ items
  };

  const tooltipHeight = getTooltipHeight(itemCount);

  // Render tooltip
  return (
    <div
      className="color-tooltip-container"
      style={{
        top: `${Math.min(position.y + 25, window.innerHeight - tooltipHeight)}px`,
        left: `${Math.min(position.x + 25, window.innerWidth - 180)}px`,
        willChange: 'transform',
      }}
    >

      {colorInfo.isGradient ? 
        (
          // Gradient display with multiple colors
          <>
            {colorInfo.gradientMatches.map((match, index) => (
              <div className='color-tooltip' key={index} style={{ marginBottom: index < colorInfo.gradientMatches.length - 1 ? '10px' : '0' }}>
                <div
                  className='color-preview'
                  style={{
                    backgroundColor: match.hexValue || match.colorValue,
                  }}
                ></div>

                <div className="color-content">
                  <p className='color-variable'><strong>{match.variableName && match.variableName.slice(1)}</strong></p>                 
                  <p className='color-type'>{match.propertyName && ` ${getDisplayPropertyName(match.propertyName)}`}</p>
                  <p className="color-value">
                    <code>{match.hexValue || rgbToHex(match.colorValue)}</code>
                  </p>
                </div>
              </div>
            ))}
          </>
      ) 
      
        : colorInfo.isMultiMatch ? (
          // Multiple single-color matches
          <>
            {colorInfo.matches.map((match, index) => (
              <div className='color-tooltip' key={index} style={{ marginBottom: index < colorInfo.matches.length - 1 ? '8px' : '0' }}>
                
                <div
                  className='color-preview'
                  style={{
                    backgroundColor: match.hexValue || match.colorValue,
                  }}
                ></div>

                <div className="color-content">
                  <p className='color-variable'><strong>{match.variableName && match.variableName.slice(1)}</strong></p>
                  <p className='color-type'>{match.propertyName && ` ${getDisplayPropertyName(match.propertyName)}`}</p>
                  <p className="color-value">
                    <code>{match.hexValue || rgbToHex(match.colorValue)}</code>
                  </p>
                </div>

              </div>
            ))}
          </>
    )
    
    : (

        
      <>
        <div className="color-tooltip">
          <div
            className='color-preview'
            style={{
              backgroundColor: colorInfo.hexValue || colorInfo.colorValue,
            }}
          ></div>

          <div className="color-content">
            <p className='color-variable'><strong>{colorInfo.variableName.slice(1)}</strong></p>
            <p className='color-type'>{colorInfo.propertyName && ` ${getDisplayPropertyName(colorInfo.propertyName)}`}</p>
            <p className="color-value">
              <code>{colorInfo.hexValue || rgbToHex(colorInfo.colorValue)}</code>
            </p>
          </div>
        </div>
      </>
      )}
    </div>
  );
};

export default ColorTooltip;